import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  InputBase,
  Slide
} from "@mui/material";
import AddressDialog from "../../Components/AddressDialog/AddressDialog";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { postData, deleteData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const { cartCount } = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [menuItems, setMenuItems] = useState([{ label: "HOME", path: "/" }]);
  const [subcategories, setSubcategories] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate('/search', { state: { searchTerm } });
      setOpen(false);
      setSearchTerm("");
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Navigate on every keystroke for live search
    if (value.trim()) {
      navigate('/search', {
        state: { searchTerm: value },
        replace: location.pathname === '/search'
      });
    } else if (location.pathname === '/search') {
      navigate('/search', {
        state: { searchTerm: "" },
        replace: true
      });
    }
  };

  // Fetch categories
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData('category/list', {}, token, false);

        if (response && response.categories && Array.isArray(response.categories)) {
          const activeCategories = response.categories.filter(cat => cat.status === 'active');
          const categoryMenuItems = activeCategories.map((category) => {
            const categorySlug = category.title?.toLowerCase().replace(/\s+/g, '-') || 'category';
            return {
              label: category.title.toUpperCase(),
              path: `/${categorySlug}`,
              categoryId: category._id,
              categoryTitle: category.title,
            };
          });

          setMenuItems([
            { label: "HOME", path: "/" },
            ...categoryMenuItems
          ]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setMenuItems([{ label: "HOME", path: "/" }]);
      }
    };
    fetchCategories();
  }, []);

  // User auth check
  React.useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    const token = sessionStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
      if (storedUser) {
        try { setUser(JSON.parse(storedUser)); } catch (e) { console.error(e); }
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [location]);

  if (location.pathname === "/signup" || location.pathname === "/signin") return null;

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const fetchSubcategories = async (categoryId) => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await postData('subcategory/list', { category_id: categoryId }, token, false);
      if (response && response.subcategories) {
        setSubcategories(prev => ({ ...prev, [categoryId]: response.subcategories }));
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const handleCategoryClick = (item, event) => {
    if (window.innerWidth < 900) {
      if (item.categoryId) {
        setActiveCategory(activeCategory === item.categoryId ? null : item.categoryId);
        if (!subcategories[item.categoryId]) fetchSubcategories(item.categoryId);
        event.preventDefault();
      } else {
        navigate(item.path);
        handleDrawerToggle();
      }
    } else {
      if (item.categoryId) {
        navigate(item.path, {
          state: { categoryId: item.categoryId, categoryTitle: item.categoryTitle }
        });
      } else {
        navigate(item.path);
      }
      setOpenDropdown(null);
    }
  };

  const handleCategoryHover = (item) => {
    if (window.innerWidth >= 900 && item?.categoryId) {
      setOpenDropdown(item.categoryId);
      if (!subcategories[item.categoryId]) fetchSubcategories(item.categoryId);
    }
  };

  const handleSubcategoryClick = (subcategory) => {
    const categorySlug = subcategory.category_id.title?.toLowerCase().replace(/\s+/g, '-') || 'category';
    const subcategorySlug = subcategory.title?.toLowerCase().replace(/\s+/g, '-') || 'subcategory';
    navigate(`/${categorySlug}/${subcategorySlug}`, {
      state: {
        categoryId: subcategory.category_id._id,
        subcategoryId: subcategory._id,
        categoryTitle: subcategory.category_id.title,
        subcategoryTitle: subcategory.title
      }
    });
    setMobileOpen(false);
    setActiveCategory(null);
  };

  const handleProfileClick = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileClose = () => setProfileAnchorEl(null);

  const handleLogout = () => {
    sessionStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    handleProfileClose();
    navigate("/");
    window.location.reload();
  };

  const drawer = (
    <Box sx={{ width: 280, backgroundColor: "#111", height: "100%", color: "white" }}>
      <Box sx={{ my: 2, textAlign: "center" }}>
        <Box component="img" onClick={() => navigate("/")} src="/img/tvfmlogo.png" alt="logo" sx={{ height: 48 }} />
      </Box>

      <List>
        {menuItems.map((item, index) => (
          <Box key={item.categoryId || item.label || `menu-${index}`}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => handleCategoryClick(item, e)}
                sx={{
                  justifyContent: "space-between",
                  color: "white",
                  "&:hover": { backgroundColor: "#222" },
                  pl: 3,
                }}
              >
                <ListItemText
                  primary={item.label}
                  onClick={() => !item.categoryId && (navigate(item.path), handleDrawerToggle())}
                  sx={{ cursor: "pointer" }}
                />
                {item.categoryId && (
                  activeCategory === item.categoryId ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </ListItemButton>
            </ListItem>

            {/* Mobile Subcategories with Image */}
            {item.categoryId && activeCategory === item.categoryId && subcategories[item.categoryId] && (
              <List sx={{ pl: 2 }}>
                {subcategories[item.categoryId].map((subcategory) => (
                  <ListItemButton
                    key={subcategory._id}
                    onClick={() => handleSubcategoryClick(subcategory)}
                    sx={{
                      pl: 4,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    <Box
                      component="img"
                      src={subcategory.image || "https://via.placeholder.com/40"}
                      alt={subcategory.title}
                      onError={(e) => e.target.src = "https://via.placeholder.com/40"}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        objectFit: "cover",
                        border: "1px solid #555",
                      }}
                    />
                    <ListItemText
                      primary={subcategory.title}
                      primaryTypographyProps={{ fontSize: "0.95rem", color: "white" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" sx={{ background: "#222", boxShadow: "none" }}>
        <Toolbar sx={{ minHeight: 60, px: { xs: 2, sm: 4 } }}>
          {/* Logo */}
          <Typography variant="h6" component="div" sx={{ flexShrink: 0 }}>
            <Box component="img" onClick={() => navigate("/")} src="/img/tvfmlogo.png" alt="logo" sx={{ height: 48 }} />
          </Typography>

          {/* Desktop Menu - 5 items per row */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
              justifyContent: "center",
              gap: { md: 1, lg: 2 },
              flexWrap: "wrap",
              rowGap: 2,
              px: 4,
              position: "relative",
            }}
          >
            {menuItems.map((item, index) => (
              <Box
                key={item.categoryId || item.label || `menu-${index}`}
                sx={{ position: "relative" }}
                onMouseEnter={() => handleCategoryHover(item)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    color="inherit"
                    component={Link}
                    to={item.path}
                    state={item.categoryId ? { categoryId: item.categoryId, categoryTitle: item.categoryTitle } : null}
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "2px",
                      color: "white",
                      "&:hover": { color: "#e6c10dff", backgroundColor: "transparent" },
                      px: { md: 1.5, lg: 2 },
                      py: 1,
                      fontSize: { md: "0.9rem", lg: "1rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Button>
                  {item.categoryId && (
                    <IconButton size="small" sx={{ color: "white", ml: -1 }}>
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Desktop Dropdown with Image + Title */}
                {item.categoryId && openDropdown === item.categoryId && subcategories[item.categoryId] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "#111",
                      boxShadow: 6,
                      borderRadius: 2,
                      zIndex: 10,
                      minWidth: 300,
                      maxWidth: 360,
                      maxHeight: "70vh",
                      overflowY: "auto",
                      // py: 2,
                      // mt: 1,
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": { background: "#555", borderRadius: 3 },
                    }}
                    onMouseEnter={() => setOpenDropdown(item.categoryId)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {subcategories[item.categoryId].map((subcategory) => (
                      <Button
                        key={subcategory._id}
                        component={Link}
                        to={`/${item.label.toLowerCase().replace(/\s+/g, '-')}/${subcategory.title.toLowerCase().replace(/\s+/g, '-')}`}
                        state={{
                          categoryId: item.categoryId,
                          subcategoryId: subcategory._id,
                          categoryTitle: item.label,
                          subcategoryTitle: subcategory.title,
                        }}
                        onClick={() => setOpenDropdown(null)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                          justifyContent: "flex-start",
                          color: "white",
                          textAlign: "left",
                          // px: 3,
                          // py: 2,
                          textTransform: "none",
                          fontSize: "0.95rem",
                          borderRadius: 1,
                          transition: "all 0.2s",
                          "&:hover": {
                            backgroundColor: "#333",
                            color: "#e6c10dff",
                            pl: 4,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={subcategory.image || "https://via.placeholder.com/40"}
                          alt={subcategory.title}
                          onError={(e) => e.target.src = "https://via.placeholder.com/40"}
                          sx={{
                            width: 44,
                            height: 44,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid #444",
                            flexShrink: 0,
                          }}
                        />
                        <span>{subcategory.title}</span>
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Right Icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: { xs: "auto", md: 2 } }}>
            <SearchIcon sx={{ color: "white", cursor: "pointer" }} onClick={() => setOpen(!open)} />

            <Slide direction="down" in={open} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  display: "flex",
                  justifyContent: "center",
                  py: 3,
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    pointerEvents: "auto",
                    width: "100%",
                    maxWidth: 600,
                    bgcolor: "rgba(255, 255, 255, 1)",
                    borderRadius: "50px",
                    display: "flex",
                    alignItems: "center",
                    px: 3,
                    py: 1.2,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                  }}
                >
                  <SearchIcon sx={{ color: "#999", mr: 2 }} />
                  <InputBase
                    placeholder="Search products..."
                    autoFocus
                    sx={{ flex: 1, fontSize: "1.1rem" }}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={handleSearchKeyPress}
                  />
                  <IconButton onClick={() => setOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </Slide>


            <Box sx={{ position: 'relative' }}>
              <IconButton color="inherit" component={Link} to="/addtocart">
                <ShoppingCartIcon sx={{ color: "white" }} />
              </IconButton>
              {cartCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '#e6c10dff',
                    color: 'black',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    transform: 'translate(25%, -25%)',
                  }}
                >
                  {cartCount > 9 ? '9+' : cartCount}
                </Box>
              )}
            </Box>

            <IconButton color="inherit" onClick={handleProfileClick} sx={{ p: 0 }}>
              {user?.name ? (
                <Avatar sx={{ width: 32, height: 32, bgcolor: "#e6c10dff", color: "#000", fontWeight: "bold" }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircleIcon sx={{ color: "white" }} />
              )}
            </IconButton>

            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileClose}
              PaperProps={{ sx: { bgcolor: "#111", color: "white", mt: 1, minWidth: 160 } }}
            >
              {/* Profile Menu Items */}
              {isAuthenticated && [
                <MenuItem key="profile" component={Link} to="/profile" onClick={handleProfileClose}>Profile</MenuItem>,
                <MenuItem key="address" onClick={() => { handleProfileClose(); setAddressDialogOpen(true); }}>Saved Address</MenuItem>,
                <MenuItem key="wishlist" component={Link} to="/wishlist" onClick={handleProfileClose}>Wishlist</MenuItem>,
                <MenuItem key="orders" component={Link} to="/orders" onClick={handleProfileClose}>Orders</MenuItem>,
                <MenuItem key="rewards" component={Link} to="/rewards" onClick={handleProfileClose}>Rewards</MenuItem>,
                <MenuItem key="notification" component={Link} to="/notification" onClick={handleProfileClose}>Notification</MenuItem>,
              ]}

              <MenuItem component={Link} to="/policies" onClick={handleProfileClose}>Policies</MenuItem>
              <MenuItem component={Link} to="/faqs" onClick={handleProfileClose}>FAQs</MenuItem>
              {isAuthenticated ? (
                <MenuItem onClick={handleLogout}>Signout</MenuItem>
              ) : (
                <MenuItem component={Link} to="/signin" onClick={handleProfileClose}>Signin</MenuItem>
              )}
            </Menu>

            <AddressDialog
              open={addressDialogOpen}
              onClose={() => setAddressDialogOpen(false)}
              onSave={(selected) => { console.log(selected); setAddressDialogOpen(false); }}
              isMobile={false}
            />

            <IconButton color="inherit" onClick={handleDrawerToggle} sx={{ display: { md: "none" } }}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;